import React from 'react';
import clsx from 'clsx';
import SideDrawer from '../../../common/_custom/SideDrawer/SideDrawer';
import OfficeIcon from '../../../assets/Svg/OfficeIcon';
import Tooltip from '../../../common/_custom/Tooltip/Tooltip';
import { useLenderNetworkDrawer } from './useLenderNetworkDrawer';
import { SOURCE_LABELS, formatLenderDate } from '../../../utils/lenderConnectionUtils';
import { truncateByCharacters } from '../../../utils/utils';
import styles from './LenderNetworkDrawer.module.scss';

interface LenderNetworkDrawerProps {
    open: boolean;
    onClose: () => void;
    lenderName: string;
    pnoId: string;
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
    <span className={clsx(styles.StatusPill, status === 'ACTIVE' ? styles.StatusActive : styles.StatusClosed)}>
        {status === 'ACTIVE' ? 'Active' : 'Closed'}
    </span>
);

const ConfigDrivenPill: React.FC<{ value: string | null; options: Record<string, any> }> = ({ value, options }) => {
    if (!value) return <span className={styles.NaText}>NA</span>;
    const meta = options[value];
    const label = meta?.label || value;
    const isTruncated = label.length > 18;
    const pill = (
        <span
            className={styles.StatusPill}
            style={{ backgroundColor: meta?.backgroundColor || '#F3F4F6', color: meta?.labelColor || '#1C1B1F' }}
        >
            {truncateByCharacters(label, 18)}
        </span>
    );
    if (!isTruncated) return pill;
    return (
        <Tooltip content={label} direction="top" usePortal={true} fitContent>
            {pill}
        </Tooltip>
    );
};

const SourceChips: React.FC<{ sources: string[] }> = ({ sources }) => {
    if (!sources || sources.length === 0) return <span className={styles.NaText}>-</span>;
    return (
        <>
            {sources.map((s, i) => (
                <span key={i} className={styles.SourceChip}>
                    {SOURCE_LABELS[s] || s}
                </span>
            ))}
        </>
    );
};

const LenderNetworkDrawer: React.FC<LenderNetworkDrawerProps> = ({ open, onClose, lenderName, pnoId }) => {
    const { companies, isLoading, leadStatusOptions, relationshipStatusOptions } = useLenderNetworkDrawer(open, lenderName, pnoId);

    if (!open) return null;

    return (
        <SideDrawer
            heading={<span className={styles.DrawerHeading}>Funded Entities in Network</span>}
            onClose={onClose}
            allowClose={true}
            showHeading={true}
            width="75%"
            // Offsets SideDrawer's default right:41px to right:24px (same as RelatedCompaniesNetworkDrawer)
            iconStyle={{ marginRight: '-17px' }}
        >
            <div className={styles.DrawerContent}>
                {isLoading ? (
                    <div className={styles.EmptyState}>Loading...</div>
                ) : companies.length === 0 ? (
                    <div className={styles.EmptyState}>No companies found</div>
                ) : (
                    <div className={styles.Table}>
                        <div className={styles.HeaderRow}>
                            <span className={styles.HeaderCell}>Company Name</span>
                            <span className={styles.HeaderCell}>Connection Status</span>
                            <span className={styles.HeaderCell}>Lead Status</span>
                            <span className={styles.HeaderCell}>Relationship Status</span>
                            <span className={styles.HeaderCell}>Data Source</span>
                            <span className={styles.HeaderCell}>Data as of</span>
                        </div>
                        <div className={styles.TableBody}>
                            {companies.map((company: any, index: number) => {
                                const orgName = company.orgName || '-';
                                const isNameTruncated = orgName.length > 25;
                                const companyPill = (
                                    <span className={styles.CompanyPill}>
                                        {company.orgName && (
                                            <OfficeIcon
                                                fill="#1C1B1F"
                                                width={16}
                                                height={13}
                                                style={{ flexShrink: 0, minWidth: 16, minHeight: 13 }}
                                            />
                                        )}
                                        <span className={styles.CompanyPillText}>{truncateByCharacters(orgName, 25)}</span>
                                    </span>
                                );
                                return (
                                    <div
                                        key={company.orgId || index}
                                        className={clsx(styles.DataRow, index < companies.length - 1 && styles.DataRowBorder)}
                                    >
                                        <div className={styles.Cell}>
                                            {isNameTruncated ? (
                                                <Tooltip content={orgName} direction="top" usePortal={true} fitContent>
                                                    {companyPill}
                                                </Tooltip>
                                            ) : (
                                                companyPill
                                            )}
                                        </div>
                                        <div className={styles.Cell}>
                                            <StatusPill status={company.status} />
                                        </div>
                                        <div className={styles.Cell}>
                                            <ConfigDrivenPill value={company.leadStatus} options={leadStatusOptions} />
                                        </div>
                                        <div className={styles.Cell}>
                                            <ConfigDrivenPill value={company.relationshipStatus} options={relationshipStatusOptions} />
                                        </div>
                                        <div className={styles.SourcesCell}>
                                            <SourceChips sources={company.sources} />
                                        </div>
                                        <div className={styles.DateCell}>{formatLenderDate(company.latestDataAsOf)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </SideDrawer>
    );
};

export default LenderNetworkDrawer;
